import Fuse, { IFuseOptions } from 'fuse.js';
import { Example, NewsPost, SearchResult } from '../types/content';

/**
 * Unified search service for all content types
 */
export class SearchService {
  private posts: NewsPost[];
  private examples: Example[];
  private newsFuse: Fuse<NewsPost>;
  private examplesFuse: Fuse<Example>;
  
  constructor(posts: NewsPost[], examples: Example[]) {
    this.posts = posts;
    this.examples = examples;

    const newsOptions: IFuseOptions<NewsPost> = {
      keys: ['title', 'dek', 'source_name', 'topics', 'curator_note'],
      includeScore: true,
      threshold: 0.4,
    };
    this.newsFuse = new Fuse(posts, newsOptions);

    const exampleOptions: IFuseOptions<Example> = {
      keys: ['title', 'context', 'goal', 'audience', 'complexity', 'why_it_works'],
      includeScore: true,
      threshold: 0.4,
    };
    this.examplesFuse = new Fuse(examples, exampleOptions);
  }

  /**
   * Search across all content types
   */
  searchAll(query: string, limit: number = 10): SearchResult[] {
    if (query.trim().length < 2) return [];

    const newsResults = this.newsFuse.search(query);
    const exampleResults = this.examplesFuse.search(query);

    const combined = [...newsResults, ...exampleResults];

    // Sort by score (lower is better)
    combined.sort((a, b) => (a.score || 1) - (b.score || 1));

    return combined.slice(0, limit).map(result => {
      // The 'dek' property is unique to NewsPost, so we can use it as a type guard.
      if ('dek' in result.item) {
        const post = result.item as NewsPost;
        return {
          type: 'news' as const,
          slug: post.slug,
          title: post.title,
          excerpt: post.dek,
          url: `/news/${post.slug}`
        };
      } else {
        const example = result.item as Example;
        return {
          type: 'example' as const,
          slug: example.slug,
          title: example.title,
          excerpt: example.context,
          url: `/examples/${example.slug}`
        };
      }
    });
  }

  /**
   * Filter news posts with advanced criteria
   */
  filterNews(options: {
    query?: string;
    topics?: string[];
    sources?: string[];
    sortBy?: 'date' | 'relevance';
  }): NewsPost[] {
    let filtered: NewsPost[];

    // Apply search query
    if (options.query && options.query.trim().length > 0) {
      filtered = this.newsFuse.search(options.query).map(result => result.item);
    } else {
      filtered = [...this.posts];
    }

    // Apply topic filter
    if (options.topics && options.topics.length > 0 && !options.topics.includes('all')) {
      filtered = filtered.filter(post => 
        options.topics!.some(topic => post.topics.includes(topic))
      );
    }
    
    // Apply source filter
    if (options.sources && options.sources.length > 0 && !options.sources.includes('all')) {
      filtered = filtered.filter(post => 
        options.sources!.includes(post.source_name)
      );
    }

    // Sort results
    if (!options.query || options.sortBy === 'date') {
      filtered.sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime());
    }

    return filtered;
  }

  /**
   * Filter examples with advanced criteria
   */
  filterExamples(options: {
    query?: string;
    goals?: string[];
    audiences?: string[];
    complexities?: string[];
    sortBy?: 'relevance' | 'complexity';
  }): Example[] {
    let filtered: Example[];

    // Apply search query
    if (options.query && options.query.trim().length > 0) {
      filtered = this.examplesFuse.search(options.query).map(result => result.item);
    } else {
      filtered = [...this.examples];
    }

    // Apply goal filter
    if (options.goals && options.goals.length > 0 && !options.goals.includes('all')) {
      filtered = filtered.filter(example => 
        options.goals!.includes(example.goal)
      );
    }
    
    // Apply audience filter
    if (options.audiences && options.audiences.length > 0 && !options.audiences.includes('all')) {
      filtered = filtered.filter(example => 
        options.audiences!.includes(example.audience)
      );
    }
    
    // Apply complexity filter
    if (options.complexities && options.complexities.length > 0 && !options.complexities.includes('all')) {
      filtered = filtered.filter(example => 
        options.complexities!.includes(example.complexity)
      );
    }

    return filtered;
  }

  /**
   * Get unique values for filtering
   */
  getFilterOptions() {
    return {
      sources: Array.from(new Set(this.posts.map(post => post.source_name))),
      topics: Array.from(new Set(this.posts.flatMap(post => post.topics))),
      goals: Array.from(new Set(this.examples.map(ex => ex.goal))),
      audiences: Array.from(new Set(this.examples.map(ex => ex.audience))),
      complexities: Array.from(new Set(this.examples.map(ex => ex.complexity)))
    };
  }
}

/**
 * Create a search service instance
 */
export function createSearchService(posts: NewsPost[], examples: Example[]): SearchService {
  return new SearchService(posts, examples);
}